'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { Game } from '@/constants/game.const';
import { generatePathWithExistingPrefix } from '@/utils/navigation.utils';
import { getBlogPath } from '@/utils/paths';
import { GameIcon } from '../GameIcon';
import { Logo } from '../logo';
import { SocialLinks } from '../SocialLinks';

type FooterNavItems = {
  name: string;
  href: string;
  matchGamePrefix?: boolean;
  icon?: React.ReactNode;
};

const Footer = () => {
  const pathname = usePathname();

  const navigationFooter: Record<string, FooterNavItems[]> = useMemo(
    () => ({
      quicklinks: [
        {
          name: 'Home',
          href: generatePathWithExistingPrefix({
            currentPath: pathname,
            targetPath: '/',
            matchGamePrefix: true,
          }),
        },
        {
          name: 'News',
          href: generatePathWithExistingPrefix({
            currentPath: pathname,
            targetPath: '/news',
            matchGamePrefix: true,
          }),
        },
        {
          name: 'Matches',
          href: generatePathWithExistingPrefix({
            currentPath: pathname,
            targetPath: '/matches',
            matchGamePrefix: true,
          }),
        },
        { name: 'Betting HUB', href: '/hub' },
        { name: 'Tips', href: '/tips' },
        {
          name: "Pick'ems",
          href: generatePathWithExistingPrefix({
            currentPath: pathname,
            targetPath: '/pickems',
            matchGamePrefix: true,
          }),
        },
        {
          name: 'Contact us',
          href: 'mailto:info@gocore.gg',
        },
        {
          name: 'Blog',
          href: getBlogPath(),
        },
      ],
      games: [
        {
          icon: <GameIcon className="h-6 w-6" game={Game.DOTA} />,
          href: `/${Game.DOTA}`,
          name: 'DOTA',
        },
        {
          icon: <GameIcon className="h-6 w-6" game={Game.CS2} />,
          href: `/${Game.CS2}`,
          name: 'CS',
        },
      ],
      legal: [
        { name: 'Privacy policy', href: '/privacy' },
        {
          name: 'Use of Services',
          href: '/terms-of-service',
        },
        { name: 'Cookie policy', href: '/cookies' },
      ],
    }),
    [pathname]
  );

  return (
    <footer className="w-full bg-dark-gray">
      <h2 className="sr-only" id="footer-heading">
        Footer
      </h2>
      <div className="container mx-auto max-w-(--breakpoint-xl)">
        <div className="p-4 xl:grid xl:grid-cols-3 xl:gap-8">
          <div className="space-y-8">
            <Logo />

            <SocialLinks />
          </div>
          <div className="mt-16 grid grid-cols-1 gap-8 xl:col-span-2 xl:mt-0">
            <div className="md:col-span-2 md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="font-semibold text-blue-gray text-sm leading-6">
                  Quicklinks
                </h3>
                <div className="mt-4 grid grid-cols-2 grid-rows-3 gap-2">
                  {navigationFooter.quicklinks.map(({ name, href }) => (
                    <Link
                      className="text-gray text-sm leading-6 hover:text-white"
                      href={href}
                      key={name}
                    >
                      {name}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-8">
                <div className="mt-10 md:mt-0">
                  <h3 className="font-semibold text-blue-gray text-sm leading-6">
                    Legal
                  </h3>
                  <ul className="mt-4 space-y-2">
                    {navigationFooter.legal.map(({ name, href }) => (
                      <li key={name}>
                        <Link
                          className="text-gray text-sm leading-6 hover:text-white"
                          href={href}
                        >
                          {name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-10 md:mt-0">
                  <h3 className="font-semibold text-blue-gray text-sm leading-6">
                    Games
                  </h3>
                  <ul className="mt-2 grid w-[152px] grid-cols-3">
                    {navigationFooter.games.map(({ href, icon, name }) => (
                      <li key={name}>
                        <Link
                          aria-label={`Go to ${name} section`}
                          className="mt-4 flex scale-[2] items-center justify-center"
                          href={href}
                        >
                          {icon}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2 border-light-blue border-t p-4 text-gray">
          <p className="flex flex-col gap-1 font-light text-gray text-sm leading-5">
            <span className="font-semibold">GOCORE.GG</span>
            <span>
              Gambling involves risk. Please only gamble with funds that you can
              comfortably afford to lose. Whilst we do our upmost to offer good
              advice and information we cannot be held responsible for any loss
              that maybe be incurred as a result of gambling.
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
